import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  type Country,
} from 'react-phone-number-input';
import phoneCountryLabels from 'react-phone-number-input/locale/en';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import type { PublicRequestableEvent } from '@/lib/rta-api.ts';
import { RtaSubmissionController, submitRtaRequest, type RtaApplicant } from '@/lib/rta-submit.ts';
import type { EmailDomainSuggestion } from '@/lib/emailDomainSuggestion.ts';
import { EmailSuggestionButton } from '@/components/EmailSuggestionButton/EmailSuggestionButton.tsx';
import styles from './RequestToAttend.module.css';

type FieldName = 'first_name' | 'last_name' | 'phone' | 'email';
type FieldErrors = Partial<Record<FieldName, string>>;

/** Complete input-name contract for the focused booking-widget RTA form. */
export const RTA_FORM_FIELD_NAMES = [
  'first_name',
  'last_name',
  'phone',
  'email',
  'website',
] as const;

/** Props for the booking-widget Request to Attend form. */
export interface RequestToAttendFormProps {
  /** Public GraphQL endpoint shared by the eligibility fetch and submission. */
  endpoint: string;
  /** One canonical server-returned event/inventory selection. */
  selection: PublicRequestableEvent;
  /** Called after the server accepts the request. */
  onSuccess: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_COUNTRIES = getCountries().sort((countryA, countryB) => {
  if (countryA === 'US') return -1;
  if (countryB === 'US') return 1;
  return phoneCountryLabels[countryA].localeCompare(phoneCountryLabels[countryB]);
});

const validate = (applicant: RtaApplicant, phoneCountry: Country): FieldErrors => {
  const errors: FieldErrors = {};
  if (!applicant.first_name.trim()) errors.first_name = 'First name is required.';
  if (!applicant.last_name.trim()) errors.last_name = 'Last name is required.';
  if (!applicant.phone || !isValidPhoneNumber(applicant.phone, phoneCountry)) {
    errors.phone = 'Enter a valid phone number.';
  }
  if (!EMAIL_PATTERN.test(applicant.email.trim())) errors.email = 'Enter a valid email.';
  return errors;
};

/**
 * Render and submit the focused four-field booking-widget RTA form.
 *
 * The form sends one canonical selection, includes a hidden honeypot, blocks
 * duplicate submits synchronously, aborts on unmount, and focuses persistent
 * success/error feedback for keyboard and screen-reader users.
 *
 * @param props - Endpoint, canonical selection, and success callback.
 * @returns The applicant form or its accessible success state.
 */
export function RequestToAttendForm({
  endpoint,
  selection,
  onSuccess,
}: RequestToAttendFormProps): React.ReactElement {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const controllerRef = useRef(new RtaSubmissionController());
  const errorRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneCountry, setPhoneCountry] = useState<Country>('US');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [emailSuggestion, setEmailSuggestion] = useState<EmailDomainSuggestion | null>(null);
  // Whether `serverError` describes the EMAIL rather than the submission. Tracked
  // apart from the suggestion because a domain can be undeliverable with nothing
  // to suggest, and that message must retire on edit too.
  const [emailRejected, setEmailRejected] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle');

  useEffect(() => () => controllerRef.current.abort(), []);

  useEffect(() => {
    if (serverError) errorRef.current?.focus();
  }, [serverError]);

  useEffect(() => {
    if (status === 'sent') successRef.current?.focus();
  }, [status]);

  // `emailOverride` lets the "Did you mean…?" affordance re-submit with the
  // suggested address immediately, without waiting for the `email` state
  // update to flow back through a re-render first.
  const submit = useCallback(
    async (emailOverride?: string) => {
      const applicant: RtaApplicant = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone,
        email: (emailOverride ?? email).trim(),
      };
      const errors = validate(applicant, phoneCountry);
      setFieldErrors(errors);
      setServerError(null);
      setEmailSuggestion(null);
      setEmailRejected(false);
      if (Object.keys(errors).length > 0) return;

      setStatus('submitting');
      const execution = await controllerRef.current.run((signal) =>
        submitRtaRequest(
          selection,
          { applicant, phoneCountry, website },
          {
            endpoint,
            signal,
            getRecaptchaToken: async () =>
              executeRecaptcha ? executeRecaptcha('submit_event_attendance_request') : null,
          }
        )
      );

      if (execution.outcome === 'ignored') return;
      const result = execution.value;
      if (result.outcome === 'aborted') return;
      if (result.outcome === 'failed') {
        setStatus('idle');
        setServerError(result.reason);
        setEmailSuggestion(result.emailDomainSuggestion ?? null);
        setEmailRejected(result.emailRejected ?? false);
        return;
      }

      setStatus('sent');
      onSuccess();
    },
    [
      endpoint,
      email,
      executeRecaptcha,
      firstName,
      lastName,
      onSuccess,
      phone,
      phoneCountry,
      selection,
      website,
    ]
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      void submit();
    },
    [submit]
  );

  // Clicking the "Did you mean…?" affordance is the ONLY thing allowed to
  // change the email field on the user's behalf [GRO-6429] — it must never
  // happen automatically.
  const handleUseSuggestedEmail = useCallback(() => {
    if (!emailSuggestion) return;

    const suggestedEmail = emailSuggestion.suggested_email;
    setEmail(suggestedEmail);
    setEmailSuggestion(null);
    void submit(suggestedEmail);
  }, [emailSuggestion, submit]);

  // Typing their own correction retires a pending suggestion and the message it
  // came with: both were computed for the address just replaced. Left alone, the
  // pill stays clickable and would overwrite what the applicant typed with the
  // correction for a DIFFERENT address, and the sentence would condemn a domain
  // nobody has judged yet. [GRO-6429]
  //
  // Conditional, because `serverError` is this form's single error slot and also
  // carries failures unrelated to the email — clearing it on every keystroke would
  // hide those.
  //
  // The condition is NOT just "a suggestion is pending". A domain can be
  // undeliverable with nothing to suggest, and that rejection must retire on edit
  // too, or the sentence keeps condemning a domain the field no longer holds.
  // `emailRejected` is what records that the message belongs to the email at all.
  const onEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
      if (emailSuggestion || emailRejected) {
        setEmailSuggestion(null);
        setServerError(null);
        setEmailRejected(false);
      }
    },
    [emailSuggestion, emailRejected]
  );

  if (status === 'sent') {
    return (
      <div ref={successRef} className={styles.success} tabIndex={-1} aria-live="polite">
        <h2>Request received</h2>
        <p>We’ll be in touch if your request is confirmed.</p>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.heading}>
        <h2>Request to Attend</h2>
        <p>
          {selection.event_name}
          {selection.inventory_name ? ` · ${selection.inventory_name}` : ''}
        </p>
      </div>

      <div className={styles.fieldGrid}>
        <label className={styles.field}>
          <span>First name</span>
          <input
            name={RTA_FORM_FIELD_NAMES[0]}
            autoComplete="given-name"
            value={firstName}
            aria-invalid={Boolean(fieldErrors.first_name)}
            onChange={(event) => setFirstName(event.target.value)}
          />
          {fieldErrors.first_name ? (
            <span className={styles.fieldError}>{fieldErrors.first_name}</span>
          ) : null}
        </label>

        <label className={styles.field}>
          <span>Last name</span>
          <input
            name={RTA_FORM_FIELD_NAMES[1]}
            autoComplete="family-name"
            value={lastName}
            aria-invalid={Boolean(fieldErrors.last_name)}
            onChange={(event) => setLastName(event.target.value)}
          />
          {fieldErrors.last_name ? (
            <span className={styles.fieldError}>{fieldErrors.last_name}</span>
          ) : null}
        </label>

        <div className={styles.field}>
          <label htmlFor="rta-phone">Phone</label>
          <div className={styles.phoneRow}>
            <select
              className={styles.countrySelect}
              aria-label="Country code"
              autoComplete="tel-country-code"
              value={phoneCountry}
              onChange={(event) => setPhoneCountry(event.target.value as Country)}
            >
              {PHONE_COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {phoneCountryLabels[country]} (+{getCountryCallingCode(country)})
                </option>
              ))}
            </select>
            <input
              id="rta-phone"
              name={RTA_FORM_FIELD_NAMES[2]}
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              value={phone}
              aria-invalid={Boolean(fieldErrors.phone)}
              placeholder="(202) 555-0123"
              onChange={(event) => setPhone(event.target.value)}
            />
          </div>
          {fieldErrors.phone ? (
            <span className={styles.fieldError}>{fieldErrors.phone}</span>
          ) : null}
        </div>

        <label className={styles.field}>
          <span>Email</span>
          <input
            name={RTA_FORM_FIELD_NAMES[3]}
            type="email"
            autoComplete="email"
            value={email}
            aria-invalid={Boolean(fieldErrors.email)}
            onChange={onEmailChange}
          />
          {fieldErrors.email ? (
            <span className={styles.fieldError}>{fieldErrors.email}</span>
          ) : null}
        </label>
      </div>

      <div className={styles.honeypot} aria-hidden="true">
        <label htmlFor="rta-website">Website</label>
        <input
          id="rta-website"
          name={RTA_FORM_FIELD_NAMES[4]}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
        />
      </div>

      {serverError ? (
        <div ref={errorRef} className={styles.serverError} role="alert" tabIndex={-1}>
          {serverError}
        </div>
      ) : null}

      {emailSuggestion ? (
        <EmailSuggestionButton
          suggestedEmail={emailSuggestion.suggested_email}
          onAccept={handleUseSuggestedEmail}
          className={styles.suggestionPlacement}
        />
      ) : null}

      <button className={styles.submit} type="submit" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending…' : 'Submit request'}
      </button>

      <div className={styles.status} aria-live="polite">
        {status === 'submitting' ? 'Submitting your request.' : ''}
      </div>
    </form>
  );
}
